// "Good to know" — the practical things we ask everyone (backend core/needs), each optional:
// food rules and allergies, getting around, the experiences they want, what to skip, how they like to eat. A follow-up
// appears only when its chip is ticked (which allergy; how many special dinners).
// Look is functional for now: the final design comes with the new onboarding.
import { useEffect } from "react";
import FormShell from "../components/FormShell";

const DIET = [
  { value: "vegetarian", label: "Vegetarian" }, { value: "vegan", label: "Vegan" },
  { value: "pescatarian", label: "Fish, no meat" }, { value: "no_pork", label: "No pork" },
  { value: "halal", label: "Halal" }, { value: "no_raw_fish", label: "No raw fish" },
  { value: "gluten_free", label: "Gluten-free" }, { value: "no_alcohol", label: "No alcohol" },
];
const ALLERGIES = [
  { value: "nut_allergy", label: "Nuts" }, { value: "shellfish_allergy", label: "Shellfish" },
  { value: "egg_allergy", label: "Egg" }, { value: "soy_allergy", label: "Soy" },
  { value: "dairy_free", label: "Dairy" }, { value: "gluten_free", label: "Gluten" },
];
const NEEDS = [
  { value: "limited_walking", label: "Long walks or stairs are hard" }, { value: "wheelchair", label: "Wheelchair" },
  { value: "stroller", label: "Pushing a stroller" }, { value: "tattoos", label: "Someone has a tattoo", hint: "Most public baths turn tattoos away" },
];
const SKIP = [
  { value: "anime_gaming", label: "Anime & gaming" }, { value: "nightlife", label: "Nightlife & clubs" }, { value: "shopping", label: "Shopping" },
];
// the experiences menu (backend core/experiences): what is hard to guess is asked. Each one ticked is a request,
// answered only by a card that offers it. Same keys as EXPERIENCES there.
const EXPERIENCES = [
  { value: "sumo", label: "Sumo" }, { value: "animal_cafe", label: "Animal cafés" }, { value: "maid_cafe", label: "A maid café" },
  { value: "onsen", label: "Onsen & public baths" }, { value: "ryokan", label: "A night in a ryokan" },
  { value: "tea_ceremony", label: "Tea ceremony" }, { value: "kimono", label: "Wearing a kimono" },
  { value: "cooking_class", label: "A cooking class" }, { value: "karaoke", label: "Karaoke" },
  { value: "anime_manga", label: "Anime, manga & games" }, { value: "theme_park", label: "A theme park" },
  { value: "aquarium_zoo", label: "Aquariums & zoos" },
];
// a wish and a "not for us" that are the same thing: ticking one unticks the other (2026-10-26, both could be on)
const OPPOSITES = { "experiences:anime_manga": ["skip", "anime_gaming"], "skip:anime_gaming": ["experiences", "anime_manga"] };
// how many they are, when the group says it
const GROUP_SIZE = { solo: 1, couple: 2, honeymoon: 2 };

const EATING = [
  { value: "cheap_good", label: "Cheap and good", hint: "Counters, ramen, markets — the real everyday Japan" },
  { value: "mix", label: "A mix", hint: "Mostly casual, a nicer dinner now and then" },
  { value: "special", label: "A few dinners to remember", hint: "Kaiseki, an omakase counter — booked ahead" },
];

const label = { fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)", marginBottom: 10, display: "block" };

function Chip({ on, onClick, children, hint }) {
  return (
    <button type="button" onClick={onClick} title={hint || undefined}
      className="px-3.5 py-2 text-sm transition-all duration-150"
      style={{
        borderRadius: 999, fontWeight: on ? 600 : 500,
        background: on ? "var(--zen-text)" : "var(--zen-s1)", color: on ? "var(--zen-bg)" : "var(--zen-text)",
        border: `1.5px solid ${on ? "var(--zen-text)" : "var(--zen-border)"}`,
      }}>
      {children}
    </button>
  );
}

// the days of the trip, for a booking's date
function tripDays(dates) {
  const out = [];
  if (!dates?.startDate || !dates?.endDate) return out;
  const d = new Date(`${dates.startDate}T12:00:00`), end = new Date(`${dates.endDate}T12:00:00`);
  while (d <= end && out.length < 40) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const dayLabel = iso => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export default function PracticalPage({ onNext, onBack, startOver, practical, setPractical, dates, travelerType }) {
  const p = practical || {};
  const list = key => p[key] || [];
  const toggle = (key, value) => {
    const on = !list(key).includes(value);
    const next = { ...p, [key]: on ? [...list(key), value] : list(key).filter(v => v !== value) };
    const [otherKey, otherValue] = OPPOSITES[`${key}:${value}`] || [];
    if (on && otherKey) next[otherKey] = (next[otherKey] || []).filter(v => v !== otherValue);
    setPractical(next);
  };
  useEffect(() => {
    if (!p.group_size && GROUP_SIZE[travelerType]) setPractical({ ...p, group_size: GROUP_SIZE[travelerType] });
  }, [travelerType]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasAllergy = !!p.anyAllergy || list("allergies").length > 0;

  return (
    <FormShell
      step={6}
      photo="/photos/onboard-traveler"
      photoPos="0% 47%"
      eyebrow="GOOD TO KNOW"
      bigWord="NOTE"
      title="Anything we should know?"
      subtitle="Tick what applies — skip what doesn't. It all shapes where we send you."
      onBack={onBack}
      onStartOver={startOver}
      onContinue={onNext}
    >
      <div>
        <span className="font-mono-label" style={label}>How many of you?</span>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
            <Chip key={n} on={p.group_size === n} onClick={() => setPractical({ ...p, group_size: p.group_size === n ? 0 : n })}>
              {n === 8 ? "8+" : n}
            </Chip>
          ))}
        </div>
        {p.group_size >= 5 && (
          <p className="text-xs" style={{ color: "var(--zen-sub)", marginTop: 8 }}>
            Many of Japan's best tables are counters for six or eight — we only send you where you can sit together.
          </p>
        )}
      </div>

      <div>
        <span className="font-mono-label" style={label}>Food</span>
        <div className="flex flex-wrap gap-2">
          {DIET.map(o => <Chip key={o.value} on={list("diet").includes(o.value)} onClick={() => toggle("diet", o.value)}>{o.label}</Chip>)}
          <Chip on={hasAllergy} onClick={() => setPractical({ ...p, anyAllergy: !hasAllergy, allergies: hasAllergy ? [] : list("allergies") })}>
            A food allergy
          </Chip>
        </div>
        {hasAllergy && (
          <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: "2px solid var(--zen-accent)" }}>
            <p className="text-xs" style={{ color: "var(--zen-sub)", marginBottom: 8 }}>
              To what? We keep every table clear of it — and you'll have the sentence to show the staff.
            </p>
            <div className="flex flex-wrap gap-2">
              {ALLERGIES.map(o => <Chip key={o.value} on={list("allergies").includes(o.value)} onClick={() => toggle("allergies", o.value)}>{o.label}</Chip>)}
            </div>
          </div>
        )}
      </div>

      <div>
        <span className="font-mono-label" style={label}>Getting around</span>
        <div className="flex flex-wrap gap-2">
          {NEEDS.map(o => <Chip key={o.value} hint={o.hint} on={list("needs").includes(o.value)} onClick={() => toggle("needs", o.value)}>{o.label}</Chip>)}
        </div>
      </div>

      <div>
        <span className="font-mono-label" style={label}>Would you like any of these?</span>
        <p className="text-xs" style={{ color: "var(--zen-sub)", marginBottom: 8 }}>
          Very Japanese, and not for everyone — tick the ones you want and we'll fit them in.
        </p>
        <div className="flex flex-wrap gap-2">
          {EXPERIENCES.map(o => <Chip key={o.value} on={list("experiences").includes(o.value)} onClick={() => toggle("experiences", o.value)}>{o.label}</Chip>)}
        </div>
      </div>

      <div>
        <span className="font-mono-label" style={label}>Not for us</span>
        <div className="flex flex-wrap gap-2">
          {SKIP.map(o => <Chip key={o.value} on={list("skip").includes(o.value)} onClick={() => toggle("skip", o.value)}>{o.label}</Chip>)}
        </div>
        <input type="text" value={p.skip_text || ""} onChange={e => setPractical({ ...p, skip_text: e.target.value })}
          placeholder="Anything else to keep away from?" className="w-full"
          style={{ marginTop: 10, padding: "10px 12px", borderRadius: 4, border: "1px solid var(--zen-border)", background: "var(--zen-bg)", fontSize: 14 }} />
      </div>

      <div>
        <span className="font-mono-label" style={label}>Already booked</span>
        <p className="text-xs" style={{ color: "var(--zen-sub)", marginBottom: 8 }}>
          Tickets or tables with a day and time (teamLab, the Ghibli Museum, a dinner) — we build that day around them.
        </p>
        {(p.bookings || []).map((b, i) => {
          const set = (field, value) => setPractical({ ...p, bookings: p.bookings.map((x, j) => (j === i ? { ...x, [field]: value } : x)) });
          return (
            <div key={i} className="flex gap-2 items-center" style={{ marginBottom: 8 }}>
              <input type="text" value={b.what || ""} onChange={e => set("what", e.target.value)} placeholder="What"
                style={{ flex: 2, minWidth: 0, padding: "9px 10px", borderRadius: 4, border: "1px solid var(--zen-border)", background: "var(--zen-bg)", fontSize: 14 }} />
              <select value={b.date || ""} onChange={e => set("date", e.target.value)}
                style={{ flex: 1.2, minWidth: 0, padding: "9px 6px", borderRadius: 4, border: "1px solid var(--zen-border)", background: "var(--zen-bg)", fontSize: 13 }}>
                <option value="">Day</option>
                {tripDays(dates).map(d => <option key={d} value={d}>{dayLabel(d)}</option>)}
              </select>
              <input type="time" value={b.time || ""} onChange={e => set("time", e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: "8px 6px", borderRadius: 4, border: "1px solid var(--zen-border)", background: "var(--zen-bg)", fontSize: 13 }} />
              <button type="button" aria-label="Remove booking" onClick={() => setPractical({ ...p, bookings: p.bookings.filter((_, j) => j !== i) })}
                style={{ background: "none", border: 0, color: "var(--zen-dim)", cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
          );
        })}
        {(p.bookings || []).length < 8 && (
          <button type="button" onClick={() => setPractical({ ...p, bookings: [...(p.bookings || []), { what: "", date: "", time: "" }] })}
            className="text-sm" style={{ background: "none", border: 0, color: "var(--zen-accent)", cursor: "pointer", padding: 0, fontWeight: 600 }}>
            + Add a booking
          </button>
        )}
      </div>

      <div>
        <span className="font-mono-label" style={label}>How you like to eat</span>
        <div className="flex flex-col gap-2">
          {EATING.map(o => {
            const on = p.eating === o.value;
            return (
              <button key={o.value} type="button" onClick={() => setPractical({ ...p, eating: on ? "" : o.value })}
                className="w-full p-3 text-left"
                style={{ borderRadius: 4, background: on ? "var(--zen-s2)" : "var(--zen-s1)", border: `2px solid ${on ? "var(--zen-accent)" : "var(--zen-border)"}` }}>
                <div className="text-sm font-semibold" style={{ color: "var(--zen-text)" }}>{o.label}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--zen-sub)" }}>{o.hint}</div>
              </button>
            );
          })}
        </div>
        {p.eating === "special" && (
          <div className="flex items-center gap-3" style={{ marginTop: 10 }}>
            <span className="text-sm" style={{ color: "var(--zen-sub)" }}>How many special dinners?</span>
            {[1, 2, 3].map(n => (
              <Chip key={n} on={(p.special_dinners || 1) === n} onClick={() => setPractical({ ...p, special_dinners: n })}>{n}</Chip>
            ))}
          </div>
        )}
      </div>
    </FormShell>
  );
}
